// Meetings. Every set of notes that was read, and what it did to the board.
//
// This screen is what makes automatic task creation accountable. Tasks go live
// the moment a meeting's notes are read, with no approval step, so the least a
// system can do is show its working: which document, how many next steps it
// held, how many became new tasks, and how many were recognised as an existing
// task being chased again.
//
// A run that produced nothing is as important to see as one that produced
// twelve. A pipeline that has quietly stopped working looks exactly like a
// quiet week, and the difference only shows on this list.
import { useState } from 'react'
import { ExternalLink, RotateCcw, Undo2 } from 'lucide-react'
import { toast } from 'sonner'
import { useUndoTaskIngest } from '../../hooks/backend/tasks'
import { Button } from '../../lib/shadcn/button'
import { cn } from '../../lib/shadcn/utils'
import { longDateTime, shortDate, type IngestRun } from '../../utils/tasks'

/**
 * Every registration-shaped token this run refused to link, with its reason,
 * deduplicated across the run's next steps.
 *
 * Deduplicated because one plate discussed in a meeting typically appears in
 * several of its actions, and the same warning printed five times reads as five
 * problems.
 */
function unplacedTokens(run: IngestRun): string[] {
  const seen = new Map<string, string>()
  for (const item of run.link_report ?? []) {
    for (const u of item.unplaced ?? []) {
      if (!seen.has(u.token)) {
        seen.set(
          u.token,
          `${u.token} (${u.reason}${u.candidates && u.candidates.length > 0 ? `: ${u.candidates.join(', ')}` : ''})`,
        )
      }
    }
  }
  return Array.from(seen.values())
}

export function MeetingRuns({
  runs,
  isAdmin,
  onChanged,
}: {
  runs: IngestRun[]
  isAdmin: boolean
  onChanged: () => void
}) {
  const undo = useUndoTaskIngest()
  const [busyId, setBusyId] = useState<string | null>(null)

  const doUndo = async (run: IngestRun) => {
    setBusyId(run.id)
    try {
      const res = (await undo.trigger({ run_id: run.id })) as {
        deleted: number
        kept: number
        kept_titles: string[]
      } | null
      if (res) {
        const kept =
          res.kept > 0
            ? ` ${res.kept} were kept because somebody had already worked on them: ${res.kept_titles.join(', ')}.`
            : ''
        toast.success(`Removed ${res.deleted} task${res.deleted === 1 ? '' : 's'}.${kept}`)
      }
      onChanged()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'The import was not undone.')
    } finally {
      setBusyId(null)
    }
  }

  if (runs.length === 0) {
    return (
      <div className="p-6">
        <p className="text-sm text-muted-foreground">
          No meeting notes have been read yet. The scan looks for a new "Notes by Gemini" document
          for Management &amp; Admin Check-In, Daily Goal Evaluation, Management meeting and Weekly
          Vehicle Allocation Meeting.
        </p>
      </div>
    )
  }

  return (
    <div className="divide-y divide-border">
      {runs.map((r) => (
        <div key={r.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="truncate text-sm font-medium">
                {r.source_label ?? r.drive_file_title}
              </span>
              <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                {shortDate(r.meeting_date)}
              </span>
              {r.doc_url && (
                <a
                  href={r.doc_url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex shrink-0 items-center gap-1 text-xs text-muted-foreground underline hover:text-foreground"
                >
                  notes
                  <ExternalLink className="h-3 w-3" />
                </a>
              )}
              {r.undone_at && (
                <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">
                  undone by {r.undone_by}
                </span>
              )}
            </div>
            <p className="truncate text-xs text-muted-foreground">
              read {longDateTime(r.finished_at ?? r.started_at)}
            </p>
            {r.error_text && (
              <p className="mt-1 text-xs text-rose-600 dark:text-rose-400">{r.error_text}</p>
            )}
            {/* WHAT THE VEHICLE MATCHER COULD NOT PLACE.
                Only the refusals are shown, not the successes: a link that was
                made is already visible on the task, and a registration that was
                not is visible nowhere else. This board's own migration notes
                record that three of the mis-transcribed plates in the meeting
                notes match nothing at all, and until now nobody could have known
                which three. */}
            {unplacedTokens(r).length > 0 && (
              <p className="mt-1 text-xs text-amber-700 dark:text-amber-400">
                Not linked to a vehicle: {unplacedTokens(r).join('; ')}. Add the right one on the
                task if you know which car it is.
              </p>
            )}
          </div>

          <div className="flex items-center gap-4 text-xs tabular-nums">
            <span className="text-muted-foreground">
              <span className="font-semibold text-foreground">{r.next_steps_found}</span> next steps
            </span>
            <span className="text-muted-foreground">
              <span className="font-semibold text-emerald-600 dark:text-emerald-400">
                {r.tasks_created}
              </span>{' '}
              new
            </span>
            <span
              className="text-muted-foreground"
              title="These matched a task that was already open, so the meeting posted an update on it instead of creating a second one."
            >
              <span className="font-semibold text-amber-600 dark:text-amber-400">
                {r.tasks_linked}
              </span>{' '}
              chased
            </span>
            <span
              className={cn(
                'rounded px-1.5 py-0.5 font-medium',
                r.status === 'done'
                  ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200'
                  : r.status === 'failed'
                    ? 'bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-200'
                    : 'bg-muted text-muted-foreground',
              )}
            >
              {r.status}
            </span>
          </div>

          {isAdmin && r.tasks_created > 0 && !r.undone_at && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 gap-1.5 px-2 text-xs"
              disabled={busyId === r.id}
              onClick={() => void doUndo(r)}
            >
              {busyId === r.id ? (
                <RotateCcw className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Undo2 className="h-3.5 w-3.5" />
              )}
              Undo
            </Button>
          )}
        </div>
      ))}
    </div>
  )
}
